import React, { useEffect, useState } from 'react';
import AffiliateDashboard from './components/AffiliateDashboard';
import AdminAffiliatePanel from './components/AdminAffiliatePanel';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import SupportPage from './components/SupportPage';
import { captureReferralCode } from './utils/referral';
import { auth, onAuthStateChanged, loginWithGoogle, logout } from './firebase';

const ADMIN_EMAIL = 'tracksysales@gmail.com';

export default function AppRouter({ children }) {
  // Capture ?ref=CODE from URL on app load (before anything else renders)
  useEffect(() => {
    try { captureReferralCode(); } catch (e) { /* ignore */ }
  }, []);

  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  // ── Public routes — no auth required, checked before children (auth gate) ──
  if (pathname === '/terms')   return <TermsOfService />;
  if (pathname === '/privacy') return <PrivacyPolicy />;
  if (pathname === '/support') return <SupportPage />;

  // Route affiliate.tracksyhq.com subdomain or /affiliate path to the affiliate dashboard
  if (hostname === 'affiliate.tracksyhq.com' || pathname === '/affiliate') {
    return <AffiliateDashboard />;
  }

  // Route admin.tracksyhq.com subdomain to the admin panel
  if (hostname === 'admin.tracksyhq.com') {
    return <AdminRoute />;
  }

  // Everything else: render the main app (auth gate lives inside AppWrapper)
  return children;
}

// Standalone admin panel with its own auth state and Google sign-in
function AdminRoute() {
  const [user, setUser] = useState(undefined); // undefined=loading, null=signed-out, object=signed-in
  const [denied, setDenied] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => setUser(u ?? null));
  }, []);

  const handleSignIn = async () => {
    setSigningIn(true);
    setError('');
    setDenied(false);
    try {
      const result = await loginWithGoogle();
      if (result.user.email !== ADMIN_EMAIL) {
        await logout();
        setDenied(true);
      }
      // If email matches, onAuthStateChanged will update `user` and the panel renders
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError('Sign-in failed. Please try again.');
      }
    } finally {
      setSigningIn(false);
    }
  };

  // Still resolving auth state — render nothing to avoid flash
  if (user === undefined) return null;

  // Signed in and authorised
  if (user && user.email === ADMIN_EMAIL) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a12', padding: 24 }}>
        <AdminAffiliatePanel userEmail={user.email} />
      </div>
    );
  }

  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0a0a12',
    fontFamily: 'system-ui, sans-serif',
    gap: 8,
  };

  // Wrong account signed in
  if (denied) {
    return (
      <div style={containerStyle}>
        <div style={{ color: '#c084fc', fontSize: 32, fontWeight: 800, letterSpacing: '-1px' }}>
          Tracksy
        </div>
        <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 13, marginBottom: 24 }}>
          Admin Panel
        </div>
        <div style={{
          color: '#f87171', fontSize: 14, textAlign: 'center', maxWidth: 340,
          padding: '14px 20px', background: 'rgba(248,113,113,.08)',
          borderRadius: 10, border: '1px solid rgba(248,113,113,.2)',
        }}>
          Access denied. This panel is restricted to authorized administrators.
        </div>
        <button
          onClick={() => setDenied(false)}
          style={{
            marginTop: 16, padding: '10px 20px', borderRadius: 8,
            border: '1px solid rgba(255,255,255,.12)', background: '#1e1e2e',
            color: 'rgba(255,255,255,.6)', cursor: 'pointer', fontSize: 13,
          }}
        >
          Try a different account
        </button>
      </div>
    );
  }

  // Not signed in → sign-in screen
  return (
    <div style={containerStyle}>
      <div style={{ color: '#c084fc', fontSize: 36, fontWeight: 800, letterSpacing: '-1px', marginBottom: 4 }}>
        Tracksy
      </div>
      <div style={{ color: 'rgba(255,255,255,.35)', fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 40 }}>
        Admin Panel
      </div>

      {error && (
        <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12 }}>{error}</div>
      )}

      <button
        onClick={handleSignIn}
        disabled={signingIn}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '13px 28px', borderRadius: 10,
          border: '1px solid rgba(255,255,255,.14)',
          background: '#1a1a2e', color: '#fff',
          cursor: signingIn ? 'not-allowed' : 'pointer',
          fontSize: 15, fontWeight: 500,
          opacity: signingIn ? 0.6 : 1,
          transition: 'opacity .15s',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
          <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
          <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
          <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
        </svg>
        {signingIn ? 'Signing in…' : 'Sign in with Google'}
      </button>
    </div>
  );
}
